import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Clock,
  TrendingDown,
  Users,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  Bell,
  Heart,
  Globe,
  Zap,
  FileText,
  Award,
  Shield,
} from "lucide-react";
import { useEffect } from "react";

const NGOAdvocacyLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for NGOs & Advocacy Groups - Real-Time Public Sentiment Platform";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/nationcard-hero5.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#248405]/90 to-[#345e53]/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Heart size={16} className="mr-2" />
                    <span>Trusted by NGOs Worldwide</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-yellow-300">NGOs</span> &{" "}
                      <span className="text-orange-300">Advocacy Groups</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Mobilize support and demonstrate your impact with
                      on-demand public sentiment insights.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-yellow-300">
                      Evidence-backed advocacy for maximum community impact
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-yellow-500 hover:bg-yellow-400 text-green-900 h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href = "/solutions/ngos-advocacy")
                      }
                    >
                      Request a Demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Download Impact Stories
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>500+ NGOs served</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart size={16} />
                      <span>Social Impact Focused</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>Live Community Insights</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <Heart size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Community Sentiment
                          </div>
                          <div className="text-white/60 text-sm">
                            Advocacy Dashboard
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Charts */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Campaign Support
                        </div>
                        <div className="text-green-400 text-2xl font-bold">
                          89%
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="bg-green-400 h-2 rounded-full w-5/6"></div>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white/80 text-xs mb-2">
                          Active Supporters
                        </div>
                        <div className="text-yellow-400 text-2xl font-bold">
                          12.5K
                        </div>
                        <div className="text-white/60 text-xs mt-1">
                          +18% this month
                        </div>
                      </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Recent Activity
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Climate action support +25%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span>Petition momentum +12%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/70">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span>Donor engagement +30%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating verification indicator */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Verified Impact</div>
                  <div className="text-yellow-300 font-bold">99.8%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Your Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Traditional research methods leave advocacy groups without the
                agility to respond to changing public sentiment
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <BarChart3 className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Proving Community Support
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Difficulty demonstrating grassroots backing with hard data
                    for donors and policymakers
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Clock className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    High Cost Research
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Expensive custom surveys with long lead times delay critical
                    advocacy campaigns
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <TrendingDown className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Rapidly Shifting Concerns
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Public priorities change during crises, leaving campaigns
                    misaligned with community needs
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8 pb-6">
                  <Users className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    Diverse Stakeholder Groups
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Challenges tailoring messages effectively across different
                    demographic and interest groups
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How We Empower Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                How We Empower Your Cause
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform how you understand and mobilize community support for
                lasting social change
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                    <BarChart3 className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Issue-Specific Sentiment Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Monitor public opinion on your key issues in real time
                    across demographics, regions, and social platforms to guide
                    strategic decisions.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                    <Bell className="h-8 w-8 text-orange-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Custom Alerts & Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Get instant updates when support spikes or wanes, enabling
                    rapid response to capitalize on momentum or address
                    concerns.
                  </p>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <Users className="h-8 w-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-xl">
                    Affordable On-Demand Surveys
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Launch quick polls to thousands of verified respondents at a
                    fraction of traditional research costs with results in
                    hours.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 ">
              <h2 className="text-3xl md:text-4xl font-bold text-nation-blue mb-6">
                Key Benefits & Capabilities
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 ">
              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Zap className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="font-medium">
                  Pivot campaigns when community needs change
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <FileText className="h-6 w-6 text-blue-600 flex-shrink-0" />
                <span className="font-medium">
                  Evidence-backed reports for donors & stakeholders
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Award className="h-6 w-6 text-orange-600 flex-shrink-0" />
                <span className="font-medium">
                  Low-cost, scalable survey options
                </span>
              </div>

              <div className="flex items-center space-x-3 bg-white p-4 rounded-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <Shield className="h-6 w-6 text-purple-600 flex-shrink-0" />
                <span className="font-medium">
                  Seamless CRM & email tool integration
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Story Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <div className="text-center">
                  <Badge className="mb-4 bg-green-100 text-green-700">
                    Impact Story
                  </Badge>
                  <CardTitle className="text-2xl md:text-3xl text-nation-blue">
                    How Save the Seas Used NationCard™ to Rally Support
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      200%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Increase in petition signatures
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      48h
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Real-time alerts during oil spill response
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-emerald-600 mb-2">
                      $2.1M
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Additional funding secured with data
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50"
                  >
                    Read Full Impact Story
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-nation-blue mb-8">
                Trusted by NGOs & Advocacy Groups Worldwide
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60 mb-12">
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Environmental Justice Alliance
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Community Action Network
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Global Health Initiative
                </div>
                <div className="h-12 bg-gray-300 rounded flex items-center justify-center text-sm font-medium">
                  Human Rights Watch Coalition
                </div>
              </div>

              <Card className="max-w-2xl mx-auto hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <CardContent className="pt-8">
                  <blockquote className="text-lg italic text-muted-foreground mb-6">
                    "NationCard™ transformed our advocacy strategy. Real-time
                    sentiment tracking helped us identify the perfect moment to
                    launch our climate action campaign, resulting in
                    unprecedented community engagement and policy wins."
                  </blockquote>
                  <div className="text-center">
                    <div className="font-semibold">Dr. Elena Martinez</div>
                    <div className="text-sm text-muted-foreground">
                      Executive Director
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Climate Action Coalition
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#248405]/90 to-[#345e53]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Amplify Your Impact with NationCard™
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ready to mobilize your community with data-driven insights? See
              how real-time sentiment can supercharge your advocacy efforts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-white text-green-600 hover:bg-white/90 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Schedule Impact Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default NGOAdvocacyLanding;
